import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { ViewMore } from '@liveauctioneers/hammer-ui-core/viewMore';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { ComponentProps, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { CurrentOpenMenu, MAX_FILTER_OPTIONS_COUNT } from '@/pages/Search/SearchPage/types';
import { getRoundedNumberText } from '@/pages/Search/SearchPage/utils/getRoundedNumberText';
import { useAppSelector } from '@/redux/hooks';
import { getAggregationsOnDemandStatus } from '@/redux/modules/search/search.selectors';
import { ElasticAggregateDemandStatus } from '@/redux/modules/search/search.types';
import { getMergedOptionFacet, getSearchFacets } from '@/redux/modules/searchFacetAggregates.selectors';
import { FacetOption } from '@/types/FacetOption';
import { FilterAction } from '@/types/SearchFacet';
import { QuickFilterName, useTrackQuickFilterPillClicked } from '@/utils/amplitude/analytics/searchPage';
import { getBrowseDimensionKeyFromFacetType } from '@/utils/browsePath/browsePath';
import { modifyParamsRemovePage, modifyParamsWhenChecked, parseQueryParams } from '@/utils/queryParams';
import {
    filterFlatFacetOptionsOwnedByPrecedence,
    isFacetOptionCheckedForSemanticUrl,
    isFlatFacetOptionCheckedForCategoryIdUrl,
} from '@/utils/search/searchQuery';
import { urlSafeTitle } from '@/utils/urls';

import { ConditionalMenuContainer } from '../ConditionalMenuContainer/ConditionalMenuContainer';

type Props = {
    facetId: string;
    /**
     * Hardcoded flat facets (stylePeriod, creator, materialsAndTechniques) pass
     * their dedicated FilterAction values. Dynamic Attrs.* facets (subject)
     * omit these and fall back to the generic AttrFacet* actions — same
     * defaults `AttributeFacetFilter` uses in the AllFilters drawer.
     */
    filterActionChanged?: FilterAction;
    filterActionRemoved?: FilterAction;
    label: string;
    onMenuClose: () => void;
    onMenuOpen: (currentMenu: CurrentOpenMenu) => void;
    openMenuId: CurrentOpenMenu;
    currentOpenMenu?: CurrentOpenMenu;
    quickFilterName: QuickFilterName;
};

/**
 * Multi-select facet pill rendered in the SearchFilters row as a chip +
 * dropdown menu (one of N per query). Handles hardcoded flat facets and
 * dynamic Attrs.* facets via the same primitive — wrappers bind facetId,
 * label, and (for hardcoded facets only) FilterAction analytics.
 */
export const FacetOptionsPill = ({
    currentOpenMenu,
    facetId,
    filterActionChanged = FilterAction.AttrFacetChanged,
    filterActionRemoved = FilterAction.AttrFacetRemoved,
    label,
    onMenuClose,
    onMenuOpen,
    openMenuId,
    quickFilterName,
}: Props) => {
    const { formatMessage } = useIntl();
    const [isExpanded, setIsExpanded] = useState(false);
    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const navigateWithParams = useNavigateSearchPageWithParams();
    const trackQuickFilterPillClicked = useTrackQuickFilterPillClicked();
    const facet = useAppSelector((state) => getMergedOptionFacet(state, facetId));
    const rawFacets = useAppSelector(getSearchFacets);
    const aggregationsOnDemandStatus = useAppSelector(getAggregationsOnDemandStatus);
    const { options: mergedOptions = [] } = facet;

    const options = useMemo(
        () => filterFlatFacetOptionsOwnedByPrecedence<FacetOption>(mergedOptions, rawFacets, facetId),
        [mergedOptions, rawFacets, facetId]
    );

    const isOptionChecked = useCallback(
        (option: FacetOption) => {
            const checkedFromLegacyCategoryId = isFlatFacetOptionCheckedForCategoryIdUrl(
                queryParams as Record<string, unknown>,
                option.id,
                facetId,
                rawFacets,
                option.selected
            );
            return isFacetOptionCheckedForSemanticUrl(
                queryParams as Record<string, unknown>,
                facetId,
                option,
                checkedFromLegacyCategoryId
            );
        },
        [queryParams, rawFacets, facetId]
    );

    const onOptionClick = useCallback(
        (option: FacetOption) => {
            const newCheckStatus = !isOptionChecked(option);
            // Semantic browse facets (creator, style, material, origin) want a
            // SEO-friendly slug in the URL; dynamic Attrs.* facets fall through
            // to the raw option id since they have no semantic-URL mapping yet.
            const isSemanticBrowseFacet = Boolean(getBrowseDimensionKeyFromFacetType(facetId));
            const valueForUrl = isSemanticBrowseFacet ? urlSafeTitle(option.name ?? String(option.id)) : option.id;

            let newParams = modifyParamsWhenChecked(queryParams, facetId, valueForUrl, newCheckStatus, {
                useSemanticAttributeParams: true,
            });
            newParams = modifyParamsRemovePage(newParams);

            navigateWithParams({
                browseFacetOptionRemovedId: newCheckStatus ? undefined : option.id,
                // Hold this pill's menu open across navigation so users can
                // toggle multiple options without re-clicking the chip.
                currentOpenMenu: openMenuId,
                filterAction: newCheckStatus ? filterActionChanged : filterActionRemoved,
                params: newParams,
                preventScrollReset: true,
            });
        },
        [
            filterActionChanged,
            filterActionRemoved,
            isOptionChecked,
            navigateWithParams,
            openMenuId,
            queryParams,
            facetId,
        ]
    );

    const filteredOptions = useMemo(() => {
        const sortedOptions = options
            .filter((option) => option.count !== 0 || isOptionChecked(option))
            .sort((a, b) => b.count - a.count);
        if (!isExpanded) {
            return sortedOptions.slice(0, MAX_FILTER_OPTIONS_COUNT);
        }
        return sortedOptions;
    }, [isExpanded, options, isOptionChecked]);

    const handleMenuOpen: ComponentProps<typeof ConditionalMenuContainer>['onMenuOpen'] = useCallback(
        (menu) => {
            // Track only the chip-open click, not each option toggle.
            trackQuickFilterPillClicked(quickFilterName);
            onMenuOpen(menu);
        },
        [onMenuOpen, quickFilterName, trackQuickFilterPillClicked]
    );

    // Empty options has two shapes:
    //   - Idle / Loading: skipAggs initial load, options not yet hydrated.
    //     Show a placeholder chip whose click triggers fetchSearchAggregationsOnDemand
    //     via the parent's handleMenuOpen. Without the placeholder, the user has
    //     no chip to click and the fetch never fires.
    //   - Loaded / Error: facet genuinely empty (or fetch failed). Hide the pill —
    //     no recovery path.
    if (filteredOptions.length === 0) {
        const aggregationsAreHydrating =
            aggregationsOnDemandStatus === ElasticAggregateDemandStatus.Idle ||
            aggregationsOnDemandStatus === ElasticAggregateDemandStatus.Loading;
        if (aggregationsAreHydrating) {
            return (
                <ConditionalMenuContainer
                    currentOpenMenu={currentOpenMenu}
                    currentOpenMenuId={openMenuId}
                    onMenuClose={onMenuClose}
                    onMenuOpen={handleMenuOpen}
                    title={label}
                    trigger={<Chip style="singleFilter">{label}</Chip>}
                >
                    <Loading />
                </ConditionalMenuContainer>
            );
        }
        return null;
    }

    const hasSelected = filteredOptions.some(isOptionChecked);

    return (
        <ConditionalMenuContainer
            currentOpenMenu={currentOpenMenu}
            currentOpenMenuId={openMenuId}
            onMenuClose={onMenuClose}
            onMenuOpen={handleMenuOpen}
            title={label}
            trigger={
                <Chip
                    selected={hasSelected}
                    style="singleFilter"
                >
                    {label}
                </Chip>
            }
        >
            <ViewMore
                collapseText={formatMessage({ id: 'view_less' })}
                expandText={formatMessage({ id: 'view_more' })}
                fadeBackgroundColor="#fff"
                onChange={(expanded) => {
                    setIsExpanded(expanded);
                }}
                textCutoffHeight={200}
            >
                <div
                    className={cn('flex max-h-[400px] flex-col gap-xs overflow-y-hidden', {
                        'overflow-y-auto': isExpanded,
                    })}
                >
                    {filteredOptions.map((option) => (
                        <Checkbox.Option
                            checked={isOptionChecked(option)}
                            disabled={option.count === 0 && !isOptionChecked(option)}
                            key={option.id}
                            onChange={() => {
                                onOptionClick(option);
                            }}
                        >
                            {option.name} ({getRoundedNumberText({ totalFound: option.count })})
                        </Checkbox.Option>
                    ))}
                </div>
            </ViewMore>
        </ConditionalMenuContainer>
    );
};
